import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { computeFailedPayments, resolveFailedInvoice, watchCustomers, watchInvoices } from '../lib/data.js'
import { fmtDateTime, fmtMoney } from '../lib/format.js'
import { chargeInvoice } from '../lib/stripe.js'

export default function FailedPayments() {
  const [customers, setCustomers] = useState({})
  const [invoices, setInvoices] = useState({})
  const [busyId, setBusyId] = useState(null)
  const [flash, setFlash] = useState(null)
  useEffect(() => watchCustomers(setCustomers), [])
  useEffect(() => watchInvoices(setInvoices), [])

  const list = computeFailedPayments(invoices, customers)

  const onRetry = async (invoiceId) => {
    if (busyId) return
    setBusyId(invoiceId)
    setFlash(null)
    try {
      const r = await chargeInvoice(invoiceId)
      setFlash({ kind: 'ok', msg: `Charge submitted — Stripe status: ${r.status || 'processing'}.` })
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message || 'Retry failed.' })
    } finally {
      setBusyId(null)
    }
  }

  const onResolve = async (invoiceId) => {
    if (!confirm('Mark this failed payment as handled manually? It will be removed from this list and marked paid.')) return
    setBusyId(invoiceId)
    try {
      await resolveFailedInvoice(invoiceId)
      setFlash({ kind: 'ok', msg: 'Marked as resolved.' })
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message || 'Could not resolve.' })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-extrabold">Failed Payments</h1>
        {list.length > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-extrabold">
            {list.length}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-gray-600">
        Auto-draft customers whose Stripe charge failed twice (initial attempt + retry 2 days later). Retry to fire another charge now, or resolve if you've already collected payment by other means.
      </p>

      {flash ? (
        <div className={`rounded-lg border px-3 py-2 text-sm ${flash.kind === 'ok' ? 'bg-brand-mint border-brand-green text-brand-green-deep' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {flash.msg}
        </div>
      ) : null}

      <div className="card p-0 overflow-hidden">
        {list.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            🎉 No failed payments. All auto-draft customers are caught up.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-center px-4 py-2">Attempts</th>
                  <th className="text-left px-4 py-2">First failed</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <Link to={`/customers/${row.customerId}`} className="font-bold hover:underline">
                        {row.customer?.name || row.customerName || '—'}
                      </Link>
                      <div className="text-xs text-gray-500">#{row.invoiceNumber} · {row.billingPeriod || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-bold">{fmtMoney(row.amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-100 text-red-800 text-xs font-extrabold">
                        {row.attempts || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{fmtDateTime(row.firstFailedAt)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-[200px] truncate" title={row.failReason || ''}>{row.failReason || '—'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => onRetry(row.id)}
                        disabled={busyId === row.id}
                        className="btn-gold text-xs px-3 py-1.5 mr-2">
                        {busyId === row.id ? '…' : 'Retry'}
                      </button>
                      <button
                        onClick={() => onResolve(row.id)}
                        disabled={busyId === row.id}
                        className="btn-secondary text-xs px-3 py-1.5">
                        Resolve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
